import { useEffect, useRef, useState } from "react";

// Apps Script web app URL (see ../challengeSheet.gs). CRA inlines this at
// build time, so the dev server must be restarted after changing .env.
const FORM_ENDPOINT = process.env.REACT_APP_BUILD_FORM_URL;

const stepTitles = [
  "Your information",
  "Organisation",
  "The challenge",
  "Desired outcome",
];

const organisationSizes = [
  "1–10 people",
  "11–50 people",
  "51–200 people",
  "201–1,000 people",
  "1,000+ people",
];

const challengeAreas = [
  "AI & Automation",
  "Operations",
  "Customer Experience",
  "HR & Talent",
  "Sales & Marketing",
  "Finance",
  "Data & Analytics",
  "Training & Learning",
  "New Product Development",
  "Other",
];

const urgencies = [
  "Immediately",
  "Within 1–3 months",
  "Within 3–6 months",
  "Exploring for now",
];

const FormPane = ({ active, paneRef, heading, intro, children }) => (
  <section
    className={`apl-form-pane${active ? " apl-active" : ""}`}
    ref={paneRef}
  >
    <h4>{heading}</h4>
    <p>{intro}</p>
    <div className="apl-fields">{children}</div>
  </section>
);

const Field = ({ id, label, full, children }) => (
  <div className={`apl-field${full ? " apl-full" : ""}`}>
    <label htmlFor={id}>{label}</label>
    {children}
  </div>
);

const Select = ({ options, ...props }) => (
  <select {...props}>
    <option value="">Select</option>
    {options.map((option) => (
      <option key={option}>{option}</option>
    ))}
  </select>
);

const BuildChallengeForm = () => {
  const [step, setStep] = useState(0);
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const formRef = useRef(null);
  const paneRefs = useRef([]);
  const focusNewPane = useRef(false);

  // Panes stay mounted (hidden with CSS) so answers survive Back/Continue,
  // so focus can only move once the next pane is displayed.
  useEffect(() => {
    if (!focusNewPane.current) return;
    focusNewPane.current = false;
    paneRefs.current[step].querySelector("input, select, textarea")?.focus();
  }, [step]);

  const isLastStep = step === stepTitles.length - 1;
  const percent = ((step + 1) / stepTitles.length) * 100;

  const paneProps = (index) => ({
    active: index === step,
    paneRef: (element) => (paneRefs.current[index] = element),
  });

  // Rows land in the AI Product Lab responses sheet via the Apps Script web
  // app in ../challengeSheet.gs. Form-encoded POSTs skip the CORS preflight,
  // which Apps Script can't answer.
  const submitChallenge = async () => {
    setSending(true);
    setFailed(false);

    try {
      if (!FORM_ENDPOINT) {
        throw new Error(
          "REACT_APP_BUILD_FORM_URL is not set; add the Apps Script /exec URL to .env and restart the dev server"
        );
      }

      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        body: new URLSearchParams(new FormData(formRef.current)),
      });
      const { result } = await response.json();
      if (result !== "success") throw new Error("Sheet rejected submission");
      setSubmitted(true);
    } catch (error) {
      console.error("Build challenge form:", error);
      setFailed(true);
    } finally {
      setSending(false);
    }
  };

  const handleNext = () => {
    const invalidField = Array.from(
      paneRefs.current[step].querySelectorAll("input, select, textarea")
    ).find((field) => !field.checkValidity());

    if (invalidField) {
      invalidField.reportValidity();
      return;
    }

    if (isLastStep) {
      submitChallenge();
      return;
    }

    focusNewPane.current = true;
    setStep(step + 1);
  };

  return (
    <div className="apl-form-card apl-reveal" id="apply">
      <form
        ref={formRef}
        noValidate
        onSubmit={(event) => event.preventDefault()}
      >
        <div className="apl-form-head">
          <div className="apl-form-open">
            <i />
            Lab applications open
          </div>
          <h3>Bring a challenge to the Lab</h3>
          <p>Four short steps. Plain language is perfect.</p>
          <div className="apl-form-step">
            <span>{`Step ${step + 1} of ${stepTitles.length} · ${
              stepTitles[step]
            }`}</span>
            <span>{`${Math.round(percent)}%`}</span>
          </div>
          <div className="apl-progress">
            <i style={{ width: `${percent}%` }} />
          </div>
        </div>

        <div className="apl-form-body" hidden={submitted}>
          <FormPane
            {...paneProps(0)}
            heading="Your information"
            intro="Who should we speak with about the challenge?"
          >
            <Field id="fullName" label="Full name">
              <input
                id="fullName"
                name="full_name"
                autoComplete="name"
                required
              />
            </Field>
            <Field id="workEmail" label="Work email">
              <input
                id="workEmail"
                name="work_email"
                type="email"
                autoComplete="email"
                required
              />
            </Field>
            <Field id="phone" label="Phone number">
              <input
                id="phone"
                name="phone"
                type="tel"
                autoComplete="tel"
                required
              />
            </Field>
            <Field id="jobTitle" label="Job title">
              <input id="jobTitle" name="job_title" required />
            </Field>
          </FormPane>

          <FormPane
            {...paneProps(1)}
            heading="About your organisation"
            intro="A little context helps us understand the environment."
          >
            <Field id="orgName" label="Organisation name">
              <input
                id="orgName"
                name="organisation_name"
                autoComplete="organization"
                required
              />
            </Field>
            <Field id="website" label="Website">
              <input
                id="website"
                name="website"
                type="url"
                placeholder="https://..."
              />
            </Field>
            <Field id="industry" label="Industry">
              <input id="industry" name="industry" required />
            </Field>
            <Field id="orgSize" label="Organisation size">
              <Select
                id="orgSize"
                name="organisation_size"
                options={organisationSizes}
                required
              />
            </Field>
          </FormPane>

          <FormPane
            {...paneProps(2)}
            heading="Tell us what is not working"
            intro="Describe the problem as you experience it today."
          >
            <Field id="orgDoes" label="What does your organisation do?" full>
              <textarea id="orgDoes" name="organisation_description" required />
            </Field>
            <Field
              id="problem"
              label="What problem should the Lab explore?"
              full
            >
              <textarea id="problem" name="business_problem" required />
            </Field>
            <Field id="current" label="How are you handling it today?" full>
              <textarea id="current" name="current_approach" required />
            </Field>
          </FormPane>

          <FormPane
            {...paneProps(3)}
            heading="What would better look like?"
            intro="Help us understand the impact and urgency."
          >
            <Field id="affected" label="Who is affected by this problem?" full>
              <textarea id="affected" name="people_affected" required />
            </Field>
            <Field
              id="success"
              label="What would a successful outcome look like?"
              full
            >
              <textarea id="success" name="success_outcome" required />
            </Field>
            <Field id="area" label="Challenge area">
              <Select
                id="area"
                name="problem_area"
                options={challengeAreas}
                required
              />
            </Field>
            <Field id="urgency" label="Urgency">
              <Select
                id="urgency"
                name="urgency"
                options={urgencies}
                required
              />
            </Field>
          </FormPane>

          {failed && (
            <p className="apl-form-error" role="alert">
              We couldn’t send your challenge. Check your connection and try
              again.
            </p>
          )}

          <div className="apl-form-actions">
            <button
              className="apl-form-btn apl-back"
              type="button"
              hidden={step === 0}
              disabled={sending}
              onClick={() => setStep(step - 1)}
            >
              Back
            </button>
            <button
              className="apl-form-btn apl-next"
              type="button"
              disabled={sending}
              onClick={handleNext}
            >
              {sending
                ? "Submitting…"
                : isLastStep
                ? "Submit My Challenge"
                : "Continue"}
            </button>
          </div>
        </div>

        <div className={`apl-form-status${submitted ? " apl-show" : ""}`}>
          <div className="apl-status-icon">✓</div>
          <h3>Your Lab brief is ready.</h3>
          <p>
            Thank you for bringing your challenge to the Lab. The Utiva team
            will review your brief and contact you at the email you provided.
          </p>
        </div>
      </form>
    </div>
  );
};

export default BuildChallengeForm;
